import { useCallback } from "react";
import type {
  CloseCoverageGapBody,
  CoverageGap,
  CoverageGapMember,
  UpdateCoverageGapBody,
} from "@shared/types/corpus/api.ts";
import {
  closeCoverageGap,
  createCoverageGap,
  detachClaimFromCoverageGap,
  attachClaimToCoverageGap,
  getCoverageGap,
  getCoverageGapClaims,
  getCoverageGaps,
  reopenCoverageGap,
  updateCoverageGap,
} from "../../../../lib/corpusApi";
import { useAsyncData, type AsyncDataResult } from "../../../../hooks/useAsyncData";
import {
  useAsyncPaginatedData,
  type UsePaginatedDataResult,
} from "../../../../hooks/useAsyncPaginatedData";
import { PAGE_SIZE_OPTIONS } from "../../../../lib/pagination";

const OPEN_GAPS_LIMIT = 100;

export interface UseCoverageGapResult {
  gap: AsyncDataResult<CoverageGap>;
  /** The claims of the gap. */
  members: UsePaginatedDataResult<CoverageGapMember>;
  /** The open gaps, which a claim can be moved to. */
  openGaps: CoverageGap[];
  updateGap: (changes: UpdateCoverageGapBody) => Promise<void>;
  /** Moves a claim of this gap to another one. */
  moveClaim: (claimId: string, toGapId: string) => Promise<void>;
  /** Makes a new gap and moves a claim of this one into it. */
  moveClaimToNewGap: (claimId: string, name: string, note: string) => Promise<void>;
  detachClaim: (claimId: string) => Promise<void>;
  closeGap: (body: CloseCoverageGapBody) => Promise<void>;
  reopenGap: () => Promise<void>;
  /** Loads the gap and its claims again, for the claims' answers as a check of a closed gap comes in. */
  refresh: () => Promise<void>;
}

/** One coverage gap: its figures, its claims, and what the operator does to it. Every change
 *  reloads the gap and its claims, since it moves figures in both. */
export function useCoverageGap(gapId: string): UseCoverageGapResult {
  const gap = useAsyncData(() => getCoverageGap(gapId));
  const members = useAsyncPaginatedData((page) => getCoverageGapClaims(gapId, page), {
    pageSize: 25,
    key: gapId,
  });
  const openGapsData = useAsyncData(() =>
    getCoverageGaps({ state: "open", limit: OPEN_GAPS_LIMIT })
  );

  const { refresh: refreshGap } = gap;
  const { refresh: refreshMembers } = members;

  const refresh = useCallback(async () => {
    await Promise.all([refreshGap(), refreshMembers()]);
  }, [refreshGap, refreshMembers]);

  const updateGap = useCallback(
    async (changes: UpdateCoverageGapBody) => {
      await updateCoverageGap(gapId, changes);
      await refreshGap();
    },
    [gapId, refreshGap]
  );

  const moveClaim = useCallback(
    async (claimId: string, toGapId: string) => {
      await attachClaimToCoverageGap(toGapId, claimId);
      await refresh();
    },
    [refresh]
  );

  const moveClaimToNewGap = useCallback(
    async (claimId: string, name: string, note: string) => {
      const created = await createCoverageGap({ name, note });
      await moveClaim(claimId, created.id);
    },
    [moveClaim]
  );

  const detachClaim = useCallback(
    async (claimId: string) => {
      await detachClaimFromCoverageGap(gapId, claimId);
      await refresh();
    },
    [gapId, refresh]
  );

  const closeGap = useCallback(
    async (body: CloseCoverageGapBody) => {
      await closeCoverageGap(gapId, body);
      await refresh();
    },
    [gapId, refresh]
  );

  const reopenGap = useCallback(async () => {
    await reopenCoverageGap(gapId);
    await refresh();
  }, [gapId, refresh]);

  return {
    gap,
    members: {
      ...members,
      pagination: { ...members.pagination, pageSizeOptions: PAGE_SIZE_OPTIONS },
    },
    openGaps: (openGapsData.data?.items ?? []).filter((other) => other.id !== gapId),
    updateGap,
    moveClaim,
    moveClaimToNewGap,
    detachClaim,
    closeGap,
    reopenGap,
    refresh,
  };
}
